import {Button, Input, Spinner, useDisclosure} from "@heroui/react"

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
} from "@heroui/drawer"

import AlertDescriptionItem from "./AlertDescriptionItem"
import {CreateAlertForm} from "./CreateAlertForm"
import {useAlertDefinitions} from "@/utils/hooks/useAlertDefinitions"
import {
  getAlertCategoryIcon,
  formatAlertCategoryName,
  formatAlertName,
  getAlertCategoryColors,
} from "@/features/alerts/utils/categoryUtils"
import {IconAlertTriangle, IconPlus} from "@tabler/icons-react"

const SetAlertsDrawerMenu = ({
  isOpen,
  onOpenChange,
  flag,
}: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  flag?: "compliance" | "bi"
}) => {
  // Fetch real alert definitions
  const {
    definitions: alertDefinitions,
    loading: alertsLoading,
    error: alertsError,
    refetch: refetchAlertDefinitions,
  } = useAlertDefinitions()

  // Create alert modal state
  const {
    isOpen: isCreateAlertOpen,
    onOpen: onCreateAlertOpen,
    onOpenChange: onCreateAlertOpenChange,
  } = useDisclosure()

  const handleCreateSuccess = () => {
    // Refetch alert definitions to show the new one
    refetchAlertDefinitions()
  }

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={() => {
        onOpenChange(false)
      }}
      size="4xl"
    >
      <DrawerContent>
        {(onClose) => (
          <>
            <DrawerHeader className="flex flex-col gap-1">
              <p className="text-lg font-medium">
                Intelligent AI Alerts Configuration
              </p>
              <p className="text-sm font-normal">
                Set alerts that will notify your team under certain conditions
                or violations. Quartz will look for compliance violations or
                suspicious activity.
              </p>
            </DrawerHeader>
            <DrawerBody className="flex h-fit flex-col gap-2 rounded-md border border-black/5 bg-onyx-slate/15 p-2 py-1 bg-grid-small-onyx-black/15">
              <div className="flex flex-col gap-2 border-b border-black/5 px-2 pb-1">
                <p className="text-sm font-medium">Active Alert Policies:</p>
              </div>

              {alertsError ? (
                <div className="rounded-md bg-red-50 p-4 text-red-700">
                  <p className="font-medium">Error loading alert definitions</p>
                  <p className="text-sm">{alertsError}</p>
                </div>
              ) : alertsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="lg" color="primary" />
                  <p className="ml-3 text-gray-600">
                    Loading alert definitions...
                  </p>
                </div>
              ) : alertDefinitions.length === 0 ? (
                <div className="rounded-md bg-gray-50 p-6 text-center">
                  <IconAlertTriangle
                    size={48}
                    className="mx-auto mb-3 text-gray-400"
                  />
                  <p className="text-gray-600">
                    No alert definitions configured
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Create your first alert definition to get started.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {alertDefinitions
                    .filter((categoryGroup) => {
                      // For BI alerts, only show behavioral_sentiment_analysis
                      if (flag === "bi") {
                        return (
                          categoryGroup.category ===
                          "behavioral_sentiment_analysis"
                        )
                      }
                      // For compliance alerts, show all except behavioral_sentiment_analysis
                      return (
                        categoryGroup.category !==
                        "behavioral_sentiment_analysis"
                      )
                    })
                    .map((categoryGroup) => {
                      const colors = getAlertCategoryColors(
                        categoryGroup.category
                      )

                      return (
                        <div
                          key={categoryGroup.category}
                          className="flex flex-col gap-1 overflow-hidden rounded-md border border-black/5 bg-onyx-white px-1 py-1"
                        >
                          <div className="flex w-fit flex-row items-center gap-1 rounded-xl border border-black/5 bg-white p-1">
                            <div className="ml-2 flex flex-row items-center gap-2">
                              <div className={`${colors.text}`}>
                                {getAlertCategoryIcon(
                                  categoryGroup.category,
                                  16
                                )}
                              </div>
                              <p className="font-regular mr-1 text-xs">
                                {formatAlertCategoryName(
                                  categoryGroup.category
                                )}{" "}
                                Policy
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col overflow-hidden rounded-md">
                            {categoryGroup.definitions.map((definition) => (
                              <AlertDescriptionItem
                                key={definition.alert_definition_id}
                                note={
                                  definition.description ||
                                  "No description available"
                                }
                                type={formatAlertCategoryName(
                                  categoryGroup.category
                                )}
                                subtype={formatAlertName(definition.name)}
                                idx={0}
                              />
                            ))}
                          </div>
                        </div>
                      )
                    })}
                </div>
              )}
            </DrawerBody>
            <DrawerFooter>
              <Button
                color="primary"
                variant="flat"
                size="sm"
                startContent={<IconPlus size={16} />}
                onPress={onCreateAlertOpen}
              >
                Create New Alert
              </Button>
              <Button
                color="danger"
                variant="ghost"
                size="sm"
                onPress={onClose}
              >
                Close
              </Button>
            </DrawerFooter>
          </>
        )}
      </DrawerContent>

      {/* Create Alert Form Modal */}
      <CreateAlertForm
        isOpen={isCreateAlertOpen}
        onOpenChange={onCreateAlertOpenChange}
        onSuccess={handleCreateSuccess}
      />
    </Drawer>
  )
}

export default SetAlertsDrawerMenu
